import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ShieldAlert, Bandage, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { NewReportDialog } from "@/components/incidents/NewReportDialog";
import { ReportDetailDialog } from "@/components/incidents/ReportDetailDialog";
import { StatusChip } from "@/components/incidents/StatusChip";

const fmt = d => { try { return format(parseISO(d), "d MMM yyyy"); } catch { return d; } };

export default function Incidents() {
  const location = useLocation();
  const [perms, setPerms] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [teams, setTeams] = useState([]);
  const [scope, setScope] = useState("mine");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openReport, setOpenReport] = useState(null);

  useEffect(() => {
    api.get("/incidents/permissions").then(r => { setPerms(r.data); if (r.data.review_types.length) setScope("review"); }).catch(() => {});
    api.get("/incidents/templates").then(r => setTemplates(r.data)).catch(() => {});
    api.get("/teams").then(r => setTeams(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async (s) => {
    setLoading(true);
    try { const { data } = await api.get(`/incidents?scope=${s}`); setRows(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load reports"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (perms) load(scope); }, [scope, perms, load]);

  useEffect(() => {
    const id = new URLSearchParams(location.search).get("report_id");
    if (id) api.get(`/incidents/${id}`).then(r => setOpenReport(r.data)).catch(() => {});
    const bid = new URLSearchParams(location.search).get("from_booking");
    if (bid) {
      api.get(`/bookings/${bid}`).then(({ data: b }) => {
        const hhmm = `${String(b.start_hour).padStart(2, "0")}:${String(b.start_min || 0).padStart(2, "0")}`;
        const where = `${b.venue_name ? b.venue_name + " · " : ""}${b.pitch_name || ""}`.trim();
        const fixture = `${b.age_group ? b.age_group + " " : ""}${b.team_name || ""}${b.opponent ? ` vs ${b.opponent}` : ""}${b.match_type ? ` (${b.match_type})` : ""}`.trim();
        const summary = `Fixture: ${fixture}\nDate/time: ${b.booking_date} ${hhmm}\nLocation: ${where}\nReferee: ${b.referee_name || (b.referee_tbd ? "TBA" : "none")}\nBooked by: ${b.user_name || ""}\n\n`;
        setPrefill({
          type: "incident", team_id: b.team_id || "", booking_id: b.id,
          values: { date: b.booking_date, time: hhmm, location: where, activity: b.session_type === "game" ? "Match" : "Training",
                    people: `Team: ${fixture}\nCoach: ${b.user_name || ""}\nReferee: ${b.referee_name || "TBA"}\n`, description: summary },
        });
        setCreating(true);
      }).catch(() => toast.error("Couldn't load that booking"));
    }
  }, [location.search]);
  const [prefill, setPrefill] = useState(null);

  const onChanged = (data) => { setOpenReport(data); load(scope); };
  const canReview = (perms?.review_types || []).length > 0;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="incidents-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Welfare & safety</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl mt-1">Incidents & Accidents</h1>
          <p className="opacity-60 text-sm mt-1">Report an accident or a safeguarding incident. You'll be emailed at every stage of the review.{perms?.is_welfare_officer ? " You are a Club Welfare Officer — you can review all reports." : ""}</p>
        </div>
        <button type="button" onClick={() => setCreating(true)} data-testid="new-report-btn" className="btn-volt px-5 py-3 rounded-md inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New report</button>
      </div>

      {canReview && (
        <div className="flex gap-2" data-testid="incidents-scope-tabs">
          {[{ k: "review", l: "For review" }, { k: "mine", l: "My reports" }].map(t => (
            <button key={t.k} onClick={() => setScope(t.k)} data-testid={`incidents-scope-${t.k}`} className={`px-4 py-2 rounded text-[10px] tracking-widest uppercase border ${scope === t.k ? "border-[#83B68B] bg-[#83B68B]/15 text-[#83B68B]" : "border-border hover:border-white/40"}`}>{t.l}</button>
          ))}
        </div>
      )}

      {loading ? <div className="opacity-50 font-display tracking-widest">LOADING…</div>
        : rows.length === 0 ? <div className="card-tactical rounded-md p-10 text-center opacity-60" data-testid="incidents-empty">No reports {scope === "review" ? "to review" : "yet"}.</div>
        : (
          <div className="space-y-2" data-testid="incidents-list">
            {rows.map(r => (
              <button key={r.id} type="button" onClick={() => setOpenReport(r)} data-testid={`incident-row-${r.id}`} className="w-full text-left card-tactical rounded-md p-4 hover:border-[#83B68B]/60 transition-colors">
                <div className="flex flex-wrap items-center gap-3">
                  {r.type === "accident" ? <Bandage className="w-4 h-4 text-[#83B68B]" /> : <ShieldAlert className="w-4 h-4 text-amber-400" />}
                  <span className="font-display font-bold">{r.ref}</span>
                  <span className="text-xs opacity-70 uppercase tracking-widest">{r.type}</span>
                  <StatusChip status={r.status} labels={perms?.status_labels} />
                  <span className="text-xs opacity-60 ml-auto">{r.fields?.date ? fmt(r.fields.date) : fmt(r.created_at)} · {r.reporter_name}{r.team_name ? ` · ${r.team_name}` : ""}</span>
                </div>
                <div className="text-xs opacity-70 mt-1 truncate">{r.fields?.description || r.fields?.injury || "—"}</div>
                {(r.comments || []).length > 0 && <div className="text-[10px] opacity-50 mt-1">{r.comments.length} comment{r.comments.length === 1 ? "" : "s"}</div>}
              </button>
            ))}
          </div>
        )}

      <NewReportDialog open={creating} onClose={() => { setCreating(false); setPrefill(null); }} templates={templates} teams={teams} prefill={prefill} onCreated={() => load(scope)} />
      <ReportDetailDialog report={openReport} onClose={() => setOpenReport(null)} templates={templates} perms={perms} onChanged={onChanged} />
    </div>
  );
}
